import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LoginPage } from '@/auth/LoginPage';
import { TodayPage } from '@/features/today/TodayPage';
import { MilkHistoryPage } from '@/features/milk/MilkHistoryPage';
import { MilkSessionPage } from '@/features/milk/MilkSessionPage';
import { MilkUsagePage } from '@/features/milk/MilkUsagePage';
import { MilkDayPage } from '@/features/milk/MilkDayPage';
import { AnimalsPage } from '@/features/animals/AnimalsPage';
import { AnimalFormPage } from '@/features/animals/AnimalFormPage';
import { AnimalProfilePage } from '@/features/animals/AnimalProfilePage';
import { ExitAnimalPage } from '@/features/animals/ExitAnimalPage';
import { RecordEventPage } from '@/features/events/RecordEventPage';
import { BatchHealthPage } from '@/features/events/BatchHealthPage';
import { RemindersPage } from '@/features/reminders/RemindersPage';
import { BreedingOverviewPage } from '@/features/breeding/BreedingOverviewPage';
import { MoneyPage } from '@/features/money/MoneyPage';
import { BuyersPage } from '@/features/money/BuyersPage';
import { BuyerFormPage } from '@/features/money/BuyerFormPage';
import { BuyerDetailPage } from '@/features/money/BuyerDetailPage';
import { SaleFormPage } from '@/features/money/SaleFormPage';
import { PaymentFormPage } from '@/features/money/PaymentFormPage';
import { ExpensesPage } from '@/features/expenses/ExpensesPage';
import { ExpenseFormPage } from '@/features/expenses/ExpenseFormPage';
import { RecurringExpensesPage } from '@/features/expenses/RecurringExpensesPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { SessionsSettingsPage } from '@/features/settings/SessionsSettingsPage';
import { BreedingSettingsPage } from '@/features/settings/BreedingSettingsPage';
import { ProtocolsPage } from '@/features/settings/ProtocolsPage';
import { CategoriesPage } from '@/features/settings/CategoriesPage';
import { UsersPage } from '@/features/users/UsersPage';
import { MePage } from '@/features/me/MePage';
import { MorePage } from '@/features/me/MorePage';
import { SyncIssuesPage } from '@/features/sync-issues/SyncIssuesPage';
import { Layout } from './Layout';
import { RequireAuth, RequireOwner } from './guards';
import { RouteErrorPage } from './RouteErrorPage';

// Chart-heavy screens load on demand (their chunks are still precached for offline use).
const MilkTablePage = lazy(() => import('@/features/milk/MilkTablePage').then((m) => ({ default: m.MilkTablePage })));
const DashboardPage = lazy(() => import('@/features/reports/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const MonthlyReportPage = lazy(() => import('@/features/reports/MonthlyReportPage').then((m) => ({ default: m.MonthlyReportPage })));
const CowReportPage = lazy(() => import('@/features/reports/CowReportPage').then((m) => ({ default: m.CowReportPage })));
const ForecastPage = lazy(() => import('@/features/reports/ForecastPage').then((m) => ({ default: m.ForecastPage })));
const ExportPage = lazy(() => import('@/features/reports/ExportPage').then((m) => ({ default: m.ExportPage })));
const AdoptionPage = lazy(() => import('@/features/reports/AdoptionPage').then((m) => ({ default: m.AdoptionPage })));
const HistoryPage = lazy(() => import('@/features/reports/HistoryPage').then((m) => ({ default: m.HistoryPage })));

const owner = (el: React.ReactNode) => <RequireOwner>{el}</RequireOwner>;

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    path: '/',
    errorElement: <RouteErrorPage />,
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <TodayPage /> },
      { path: 'milk', element: <MilkHistoryPage /> },
      { path: 'milk/session', element: <MilkSessionPage /> },
      { path: 'milk/usage', element: <MilkUsagePage /> },
      { path: 'milk/day/:date', element: <MilkDayPage /> },
      { path: 'milk/table', element: owner(<MilkTablePage />) },
      { path: 'animals', element: <AnimalsPage /> },
      { path: 'animals/new', element: owner(<AnimalFormPage />) },
      { path: 'animals/:id', element: <AnimalProfilePage /> },
      { path: 'animals/:id/edit', element: <AnimalFormPage /> },
      { path: 'animals/:id/exit', element: <ExitAnimalPage /> },
      { path: 'events/new', element: <RecordEventPage /> },
      { path: 'events/:eventId/edit', element: <RecordEventPage /> },
      { path: 'events/batch', element: <BatchHealthPage /> },
      { path: 'reminders', element: <RemindersPage /> },
      { path: 'breeding', element: <BreedingOverviewPage /> },
      { path: 'money', element: <MoneyPage /> },
      { path: 'buyers', element: <BuyersPage /> },
      { path: 'buyers/new', element: owner(<BuyerFormPage />) },
      { path: 'buyers/:id', element: <BuyerDetailPage /> },
      { path: 'buyers/:id/edit', element: owner(<BuyerFormPage />) },
      { path: 'sales/new', element: <SaleFormPage /> },
      { path: 'sales/:id/edit', element: <SaleFormPage /> },
      { path: 'payments/new', element: <PaymentFormPage /> },
      { path: 'payments/:id/edit', element: <PaymentFormPage /> },
      { path: 'expenses', element: <ExpensesPage /> },
      { path: 'expenses/new', element: <ExpenseFormPage /> },
      { path: 'expenses/:id/edit', element: <ExpenseFormPage /> },
      { path: 'expenses/recurring', element: owner(<RecurringExpensesPage />) },
      { path: 'dashboard', element: owner(<DashboardPage />) },
      { path: 'reports/monthly', element: owner(<MonthlyReportPage />) },
      { path: 'reports/cows/:id', element: owner(<CowReportPage />) },
      { path: 'forecast', element: owner(<ForecastPage />) },
      { path: 'export', element: owner(<ExportPage />) },
      { path: 'adoption', element: owner(<AdoptionPage />) },
      { path: 'history/:entity/:id', element: owner(<HistoryPage />) },
      { path: 'settings', element: owner(<SettingsPage />) },
      { path: 'settings/sessions', element: owner(<SessionsSettingsPage />) },
      { path: 'settings/breeding', element: owner(<BreedingSettingsPage />) },
      { path: 'settings/protocols', element: owner(<ProtocolsPage />) },
      { path: 'settings/categories', element: owner(<CategoriesPage />) },
      { path: 'settings/users', element: owner(<UsersPage />) },
      { path: 'me', element: <MePage /> },
      { path: 'more', element: <MorePage /> },
      { path: 'sync-issues', element: <SyncIssuesPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ].map((route) => ({ ...route, errorElement: <RouteErrorPage /> })),
  },
]);
